import { takeLatest, call, put, all, select } from 'redux-saga/effects';
import { toast } from 'react-toastify';
import i18next from 'i18next';
import history from '~/config/history';

import api, {
  loginConfirm,
  register,
  confirmRegister,
  recoverPassword,
  mfa,
  resendMail,
  changePassword,
  azureLogout,
} from '~/services/api';

import Tracking from '~/config/tracking';
import {
  Types as AuthTypes,
  signInSuccess,
  signInFailure,
  signOut,
  registerSuccess,
  registerFailure,
  registerConfirmationSuccess,
  registerConfirmationFailure,
  passwordRecoverySuccess,
  passwordRecoveryFailure,
  mfaSuccess,
  mfaFailure,
  resendEmailSuccess,
  resendEmailFailure,
  changePasswordSuccess,
  changePasswordFailure,
} from './actions';
import { transFormErrorResponse } from '../utils';

export const selectorSigned = state => state.auth.signed;
export const user = state => state.user;

export function* signIn(payload) {
  try {
    const signed = yield select(selectorSigned);
    if (signed) return;

    const {
      data: { token, refreshToken },
    } = yield call(loginConfirm, payload.payload);

    api.defaults.headers.Authorization = `Bearer ${token}`;

    yield put(signInSuccess(token, refreshToken));
  } catch (err) {
    yield put(signInFailure());
    Tracking.trackApiError(transFormErrorResponse(err), { showAs: 'toast' });
  }
}

export function* signOutSaga() {
  const { laliga } = yield select(user);

  yield put(signOut());
  if (laliga) {
    yield azureLogout();
  }
  history.push('/', { shouldGoToLogin: true });
}

export function* userRegister(payload) {
  try {
    const signed = yield select(selectorSigned);
    if (signed) return;

    yield call(register, payload.payload);

    yield put(registerSuccess());
    history.push(`/register-confirmation`);
    localStorage.setItem('email-confirmation', payload.payload.email);
  } catch (err) {
    yield put(registerFailure());
    Tracking.trackApiError(transFormErrorResponse(err), { showAs: 'toast' });
  }
}

export function* registerConfirmation(payload) {
  try {
    const signed = yield select(selectorSigned);
    if (signed) return;

    yield call(confirmRegister, payload.payload);

    yield put(registerConfirmationSuccess());
  } catch (err) {
    yield put(registerConfirmationFailure());
    Tracking.trackApiError(transFormErrorResponse(err), { showAs: 'toast' });
  }
}

export function* passwordRecovery(payload) {
  try {
    const signed = yield select(selectorSigned);
    if (signed) return;

    yield call(recoverPassword, payload.payload);

    yield put(passwordRecoverySuccess());
    history.push(`/`);
    toast.success(i18next.t('passwordRecoverySuccess'));
  } catch (err) {
    yield put(passwordRecoveryFailure());
    Tracking.trackApiError(transFormErrorResponse(err), { showAs: 'toast' });
  }
}

export function* resendEmail(payload) {
  try {
    const signed = yield select(selectorSigned);
    if (signed) return;

    yield call(resendMail, payload.payload);

    yield put(resendEmailSuccess());
    toast.success(i18next.t('resendEmailSuccess'));
  } catch (err) {
    yield put(resendEmailFailure());
    Tracking.trackApiError(transFormErrorResponse(err), { showAs: 'toast' });
  }
}

export function* multiFactorAuth(payload) {
  try {
    yield call(mfa, payload.payload);
    yield put(mfaSuccess());
  } catch (err) {
    yield put(mfaFailure());
    Tracking.trackApiError(transFormErrorResponse(err), { showAs: 'toast' });
  }
}

export function* resetPassword(payload) {
  try {
    yield call(changePassword, payload.payload);
    yield put(changePasswordSuccess());
    history.push(`/`);
    toast.success(i18next.t('changePasswordSuccess'));
  } catch (err) {
    yield put(changePasswordFailure());
    Tracking.trackApiError(transFormErrorResponse(err), { showAs: 'toast' });
  }
}

export function setToken({ payload }) {
  if (!payload) return false;

  const { token } = payload.auth;

  if (token) {
    api.defaults.headers.Authorization = `Bearer ${token}`;
    return true;
  }

  return false;
}

export default all([
  takeLatest('persist/REHYDRATE', setToken),
  takeLatest(AuthTypes.SIGN_IN_REQUEST, signIn),
  takeLatest(AuthTypes.SIGN_OUT_REQUEST, signOutSaga),
  takeLatest(AuthTypes.REGISTER_REQUEST, userRegister),
  takeLatest(AuthTypes.REGISTER_CONFIRMATION_REQUEST, registerConfirmation),
  takeLatest(AuthTypes.PASSWORD_RECOVERY_REQUEST, passwordRecovery),
  takeLatest(AuthTypes.MFA_REQUEST, multiFactorAuth),
  takeLatest(AuthTypes.RESEND_EMAIL_REQUEST, resendEmail),
  takeLatest(AuthTypes.CHANGE_PASSWORD_REQUEST, resetPassword),
]);
